/**
 * recoveryReport.js — отчёт о восстановлении тома, видимый НА ЭКРАНЕ телефона.
 *
 * Зачем отдельный модуль. Диагностику случая (A/B/C) писали в консоль — а на
 * мобильном консоли нет: нужен компьютер, кабель и chrome://inspect. Просить об
 * этом врача с телефоном в руках бессмысленно. Поэтому отчёт показывается прямо
 * в окне и копируется одной кнопкой.
 *
 * Записи хранятся в localStorage и переживают перезагрузку страницы. Это не
 * прихоть: мобильный Chrome умеет выгрузить фоновую вкладку целиком, и тогда
 * приложение стартует заново — в памяти не остаётся ничего, а в localStorage
 * запись есть. По ней потом и видно, что случилось именно это.
 *
 * Модуль намеренно самостоятельный: только logger, никаких зависимостей от
 * рендеринга. Сломаться здесь нечему, а работать должен даже когда всё плохо.
 */

import { log } from '../utils/logger.js';
import { mountNotice, SLOT } from './notifyHost.js';   // [9.0.2] общий столбец уведомлений

const KEY = 'mikhmel.recoveryLog.v1';
// [10.1.6] Ключ поднят до v2 нарочно: в прежних сборках метку ставили и при
// обычном закрытии страницы, поэтому у людей в памяти браузера уже лежат
// ложные v1. Со старым ключом первый же запуск новой сборки всё равно соврал
// бы про выгрузку. Старый ключ удаляем при старте, чтобы не копился мусор.
const KEY_HIDDEN = 'mikhmel.wasHidden.v2';
const KEY_HIDDEN_OLD = 'mikhmel.wasHidden.v1';
const KEY_NOTES = 'mikhmel.memoryNotes.v1';
const KEY_LEVEL = 'mikhmel.recoveryNotify.v1';

// [9.0.4] Насколько громко сообщать о восстановлении.
//
//   'off'       — молча. Журнал всё равно ведётся, отчёт доступен по кнопке.
//   'important' — только то, что требует действия: том не вернулся, вкладку выгрузили.
//   'all'       — всё, включая рядовые случаи.
//
// По умолчанию МОЛЧА. Диагностика задумывалась как временная — «покажи, что
// происходит», — а превратилась в непрерывный крик: плашка выскакивала на
// каждое сворачивание окна. Инструмент, мешающий работать, врач выключит
// целиком, и тогда мы лишимся и журнала. Поэтому громкость по умолчанию
// снята, а запись осталась.
export const NOTIFY_LEVELS = ['off', 'important', 'all'];

export function getNotifyLevel() {
  try {
    const v = localStorage.getItem(KEY_LEVEL);
    return NOTIFY_LEVELS.includes(v) ? v : 'off';
  } catch (e) { return 'off'; }
}

export function setNotifyLevel(level) {
  const v = NOTIFY_LEVELS.includes(level) ? level : 'off';
  try { localStorage.setItem(KEY_LEVEL, v); } catch (e) { /* */ }
  return v;
}
const MAX = 20;
const MAX_NOTES = 12;

const CASE_TEXT = {
  A: 'Текстура вытеснена из видеопамяти, контекст был жив. Освежили — обычный случай.',
  B: 'Контекст GPU терялся. Тома переустановлены во вьюпорты, настройки возвращены.',
  C: 'Том выгружен из памяти. Попытка перезагрузить из исходных снимков — результат ниже.',
  'C-нечем': 'Том выгружен из памяти, а исходных снимков не сохранилось. Нужно открыть серию заново.',
  'ждём': 'Контекст GPU ещё не восстановился — ждём события от браузера.',
  'нет томов': 'Показанных томов не было — восстанавливать нечего.',
  'старт-после-выгрузки': 'Приложение запустилось заново, хотя уходило в фон: вкладку выгрузил браузер (или её закрыли). Никакая починка изнутри тут не поможет — нужно сохранение состояния сеанса.',
};

/**
 * Показывать ли плашку. Правило отдельно и под тестом, потому что ошибка тут
 * стоит дорого в обе стороны: смолчать о событии — значит снова оставить
 * человека без ответа; показать лишнее — значит мозолить глаза зря.
 *
 * «Нет томов» и «ждём» — не события: ничего не произошло и показывать нечего.
 * Случай A показываем, только если действительно что-то освежили: он рядовой,
 * но пока мы не знаем, какой случай у него на телефоне, молчать о нём нельзя.
 */
const IMPORTANT = new Set(['C', 'C-нечем', 'старт-после-выгрузки']);

export function shouldNotify(rec = {}, level = getNotifyLevel()) {
  if (level === 'off') return false;
  const c = rec.case;
  if (c === 'нет томов' || c === 'ждём') return false;
  if (level === 'important') return IMPORTANT.has(c);
  if (c === 'A') return Number(rec.volumes) > 0;
  return true;
}

function _read() {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; }
}

// ── хлебные крошки: КТО тронул память ───────────────────────────────────────
//
// Отчёт с устройства назвал следствие — «том выгружен». Причину он назвать не
// мог, а причин несколько: полная очистка кэша при загрузке следующего тома,
// вытеснение кадров «Стражем», нехватка места под сам том, или браузер отнял
// память у фоновой вкладки. Средства у них разные, и гадать больше не будем.
//
// Крошки не событие восстановления: плашку не показывают, в список случаев не
// лезут. Они лежат отдельным коротким списком и печатаются в конце отчёта.

function _readNotes() {
  try {
    const raw = localStorage.getItem(KEY_NOTES);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; }
}

/** Отметить действие над памятью: кто и что сделал. */
export function noteMemoryEvent(who, what, detail = '') {
  try {
    const arr = _readNotes();
    arr.push({
      t: new Date().toISOString().replace('T', ' ').slice(11, 19),
      who: String(who || '?'), what: String(what || ''), detail: String(detail || '').slice(0, 120),
    });
    localStorage.setItem(KEY_NOTES, JSON.stringify(arr.slice(-MAX_NOTES)));
  } catch (e) { /* переполнение — не беда */ }
}

export function getMemoryNotes() { return _readNotes(); }

function _write(arr) {
  try { localStorage.setItem(KEY, JSON.stringify(arr.slice(-MAX))); } catch (e) { /* переполнение — не беда */ }
}

/** Сведения об устройстве — чтобы отчёт был самодостаточным. */
function _device() {
  try {
    const n = navigator || {};
    return {
      ua: String(n.userAgent || '').slice(0, 180),
      mem: n.deviceMemory ? `${n.deviceMemory} ГБ` : '—',
      cores: n.hardwareConcurrency || '—',
      screen: (globalThis.screen ? `${screen.width}×${screen.height}` : '—'),
      dpr: globalThis.devicePixelRatio || 1,
    };
  } catch (e) { return {}; }
}

/** Записать событие восстановления. Возвращает саму запись. */
export function logRecovery(entry = {}) {
  const rec = {
    t: new Date().toISOString().replace('T', ' ').slice(0, 19),
    case: entry.case || '—',
    action: entry.action || '',
    volumes: entry.volumes ?? entry.missing ?? null,
    reason: entry.reason || '',
    // [7.0.60] Чем кончилось. Раньше писали только что СОБИРАЛИСЬ сделать.
    outcome: entry.outcome || '',
    ok: typeof entry.ok === 'boolean' ? entry.ok : null,
    ver: (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '',
  };
  const arr = _read();
  arr.push(rec);
  _write(arr);
  try { log.info(`[recover] ${rec.case}: ${rec.action}`); } catch (e) { /* */ }
  return rec;
}

/** Полный текст отчёта — то, что человек пришлёт. */
export function getRecoveryReport() {
  const arr = _read();
  const d = _device();
  const head = [
    'ОТЧЁТ О ВОССТАНОВЛЕНИИ ТОМА',
    `Версия: ${(typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '—'}`,
    `Устройство: ${d.ua}`,
    `Память: ${d.mem} · ядер: ${d.cores} · экран: ${d.screen} @${d.dpr}x`,
    `Событий записано: ${arr.length}`,
    '',
  ];
  const notes = _readNotes();
  if (!arr.length && !notes.length) {
    head.push('Событий восстановления не было — том из памяти не пропадал.');
    return head.join('\n');
  }
  const body = arr.map((r, i) => {
    const what = CASE_TEXT[r.case] || r.action || '';
    return `${i + 1}. ${r.t}  СЛУЧАЙ ${r.case}\n   ${what}`
      + (r.volumes != null ? `\n   томов: ${r.volumes}` : '')
      + (r.outcome ? `\n   ИТОГ: ${r.ok === false ? '✗ ' : r.ok === true ? '✓ ' : ''}${r.outcome}` : '')
      + (r.reason ? `\n   повод: ${r.reason}` : '');
  });
  const tail = notes.length
    ? ['', 'ЧТО ПРОИСХОДИЛО С ПАМЯТЬЮ (кто и что тронул):']
      .concat(notes.map(n => `   ${n.t}  ${n.who}: ${n.what}${n.detail ? ' — ' + n.detail : ''}`))
    : [];
  return head.concat(body).concat(tail).join('\n');
}

/** Очистить журнал (после того как отчёт отправлен). Крошки чистятся вместе. */
export function clearRecoveryLog() {
  try { localStorage.removeItem(KEY); } catch (e) { /* */ }
  try { localStorage.removeItem(KEY_NOTES); } catch (e) { /* */ }
  return true;
}

export function hasRecoveryEvents() { return _read().length > 0; }

// ── экран ───────────────────────────────────────────────────────────────────

function _copy(text) {
  // clipboard API требует защищённого контекста; на всякий случай — запасной путь
  try {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text).then(() => true).catch(() => _copyFallback(text));
  } catch (e) { /* */ }
  return Promise.resolve(_copyFallback(text));
}
function _copyFallback(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:0';
    document.body.appendChild(ta);
    ta.select(); ta.setSelectionRange(0, text.length);
    const okc = document.execCommand?.('copy');
    ta.remove();
    return !!okc;
  } catch (e) { return false; }
}

function _esc(s) { return String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); }

/**
 * Показать отчёт на экране. Не исчезает сам — врач должен успеть прочитать и
 * скопировать; закрывается только крестиком.
 */
export function showRecoveryReport() {
  try {
    document.getElementById('recovery-report')?.remove();
    const text = getRecoveryReport();
    const arr = _read();
    const last = arr[arr.length - 1];
    const box = document.createElement('div');
    box.id = 'recovery-report';
    box.style.cssText = 'background:#101a26;color:#dfe9f5;border:1px solid #2b6;border-radius:12px;'
      + 'box-shadow:0 10px 40px rgba(0,0,0,.6);font:13px/1.5 system-ui,sans-serif;'
      + 'max-height:70vh;display:flex;flex-direction:column;overflow:hidden';
    box.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;padding:10px 12px;background:#16283a;border-bottom:1px solid #23405c">
        <b style="flex:1;font-size:14px">Отчёт о восстановлении тома</b>
        <button id="rr-close" style="background:transparent;border:1px solid #456;color:#dfe9f5;border-radius:8px;padding:6px 10px;font-size:16px;cursor:pointer">✕</button>
      </div>
      ${last ? `<div style="padding:10px 12px;background:#0e2a18;border-bottom:1px solid #234">
        <div style="font-size:15px;font-weight:700;color:#8f6">Последний случай: ${_esc(last.case)}</div>
        <div style="opacity:.9;margin-top:3px">${_esc(CASE_TEXT[last.case] || last.action || '')}</div>
      </div>` : ''}
      <pre id="rr-text" style="margin:0;padding:10px 12px;overflow:auto;white-space:pre-wrap;word-break:break-word;font:11px/1.45 ui-monospace,monospace;color:#bcd">${_esc(text)}</pre>
      <div style="display:flex;gap:8px;padding:10px 12px;border-top:1px solid #23405c;background:#16283a">
        <button id="rr-copy" style="flex:1;background:#1c7;color:#012;border:0;border-radius:8px;padding:11px;font-weight:700;font-size:14px;cursor:pointer">Скопировать отчёт</button>
        <button id="rr-share" style="flex:1;background:#37a;color:#fff;border:0;border-radius:8px;padding:11px;font-weight:700;font-size:14px;cursor:pointer">Поделиться</button>
        <button id="rr-clear" style="background:transparent;border:1px solid #456;color:#9ab;border-radius:8px;padding:11px;font-size:13px;cursor:pointer">Очистить</button>
      </div>
      <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;border-top:1px solid #23405c;background:#101a26;font-size:12px;color:#9ab">
        <span>Показывать плашки:</span>
        <select id="rr-level" style="flex:1;background:#16283a;color:#dfe9f5;border:1px solid #456;border-radius:6px;padding:6px">
          <option value="off">не показывать (журнал ведётся)</option>
          <option value="important">только важное</option>
          <option value="all">всё</option>
        </select>
      </div>`;
    mountNotice(box, SLOT.ALERT);
    box.querySelector('#rr-close').onclick = () => box.remove();
    box.querySelector('#rr-copy').onclick = async () => {
      const b = box.querySelector('#rr-copy');
      const okc = await _copy(text);
      b.textContent = okc ? '✓ Скопировано' : 'Выделите текст вручную';
      setTimeout(() => { b.textContent = 'Скопировать отчёт'; }, 2500);
    };
    box.querySelector('#rr-share').onclick = async () => {
      try {
        if (navigator.share) await navigator.share({ title: 'Отчёт о восстановлении', text });
        else { await _copy(text); box.querySelector('#rr-share').textContent = '✓ В буфере'; }
      } catch (e) { /* пользователь отменил */ }
    };
    box.querySelector('#rr-clear').onclick = () => { clearRecoveryLog(); box.remove(); };
    const sel = box.querySelector('#rr-level');
    if (sel) {
      sel.value = getNotifyLevel();
      sel.onchange = () => { setNotifyLevel(sel.value); log.info(`[recover] плашки: ${sel.value}`); };
    }
    return box;
  } catch (e) { log.warn('[recover] показ отчёта:', e.message); return null; }
}

/**
 * Короткая плашка сразу после восстановления: какой случай и кнопка «подробнее».
 * Держится 12 секунд — этого хватает заметить, но экран не загораживает.
 */
export function showRecoveryChip(rec) {
  try {
    document.getElementById('recovery-chip')?.remove();
    const c = document.createElement('div');
    c.id = 'recovery-chip';
    const bad = rec.case === 'C-нечем';
    c.style.cssText = `background:${bad ? '#5a1e1e' : '#123a22'};color:#eaf2ea;border:1px solid ${bad ? '#a44' : '#2b6'};`
      + 'border-radius:12px;padding:10px 12px;font:13px/1.4 system-ui,sans-serif;'
      + 'display:flex;align-items:center;gap:10px;box-shadow:0 8px 28px rgba(0,0,0,.5)';
    c.innerHTML = `<div style="flex:1">
        <b>Изображение восстановлено · случай ${_esc(rec.case)}</b>
        <div style="opacity:.85;font-size:12px;margin-top:2px">${_esc((CASE_TEXT[rec.case] || '').slice(0, 90))}</div>
      </div>
      <button id="rc-more" style="background:#1c7;color:#012;border:0;border-radius:8px;padding:9px 12px;font-weight:700;cursor:pointer">Отчёт</button>
      <button id="rc-off" title="Больше не показывать эти плашки" style="background:transparent;border:1px solid #567;color:#cde;border-radius:8px;padding:9px 11px;cursor:pointer;font-size:12px">Не показывать</button>
      <button id="rc-x" style="background:transparent;border:1px solid #567;color:#cde;border-radius:8px;padding:9px 11px;cursor:pointer">✕</button>`;
    mountNotice(c, SLOT.ALERT);
    c.querySelector('#rc-more').onclick = () => { c.remove(); showRecoveryReport(); };
    c.querySelector('#rc-x').onclick = () => c.remove();
    const off = c.querySelector('#rc-off');
    if (off) off.onclick = () => { setNotifyLevel('off'); c.remove(); log.info('[recover] плашки выключены — журнал и отчёт остались') };
    setTimeout(() => c.remove(), 12000);
    return c;
  } catch (e) { return null; }
}

// ── распознавание четвёртого механизма: выгрузки вкладки целиком ────────────
//
// Это не то же самое, что случаи A/B/C. Там приложение живо и чинит себя само.
// Здесь мобильный Chrome убивает фоновую вкладку под давлением памяти, и при
// возврате страница загружается с нуля: ни один обработчик восстановления даже
// не успел бы отработать — его самого не существовало.
//
// Признак прямой: перед смертью приложение ушло в фон (метка поставлена), а
// теперь стартует заново, не сняв метку. Метка снимается при возврате в
// видимое состояние, поэтому обычное сворачивание-разворачивание её не даёт.
// Ложное срабатывание остаётся одно: свернуть и закрыть браузер вручную —
// событие с точки зрения приложения то же самое, поэтому текст говорит и о нём.

function _setHidden() { try { localStorage.setItem(KEY_HIDDEN, String(Date.now())); } catch (e) { /* */ } }
function _clearHidden() { try { localStorage.removeItem(KEY_HIDDEN); } catch (e) { /* */ } }

/**
 * [9.0.0] Признак выгрузки нужен двоим — отчёту и восстановлению сеанса, — а
 * метка снимается при первом же чтении. Поэтому читаем её один раз и помним
 * ответ: кто спросит первым, теперь не имеет значения.
 */
let _discardChecked = false, _wasDiscarded = false;
export function wasDiscardedAtStart() {
  if (!_discardChecked) { _discardChecked = true; _wasDiscarded = detectDiscard(); }
  return _wasDiscarded;
}

/** Стартуем ли мы после выгрузки фоновой вкладки? Читает и сразу снимает метку. */
export function detectDiscard() {
  // [10.2.0] Сначала спрашиваем БРАУЗЕР, а не свою метку.
  //
  // document.wasDiscarded — штатный признак Chrome: true ровно тогда, когда
  // эту вкладку выгрузили и сейчас открывают заново. Он не гадает и не может
  // соврать. Своя метка была попыткой вычислить то, о чём браузер и так
  // говорит прямо, и все три моих подхода к ней (ставить на pagehide, не
  // ставить, снимать) давали ложные срабатывания при обычной перезагрузке.
  //
  // Метку оставляем ЗАПАСНЫМ путём — для браузеров без этого признака. Но
  // если браузер умеет отвечать, слушаем его и метку просто гасим.
  let browserSays = null;
  try {
    if (typeof document !== 'undefined' && 'wasDiscarded' in document) browserSays = !!document.wasDiscarded;
  } catch (e) { /* */ }

  let was = null;
  try { was = localStorage.getItem(KEY_HIDDEN); } catch (e) { return browserSays === true; }
  _clearHidden();

  if (browserSays !== null) return browserSays;
  return !!was;
}

/**
 * При старте: распознать выгрузку вкладки и показать, что было в журнале.
 */
let _inited = false;
export function initRecoveryReport() {
  // [9.0.0] Признак выгрузки теперь запоминается (его спрашивают двое), поэтому
  // повторный вызов иначе записал бы выгрузку второй раз. Один запуск — одна
  // запись.
  if (_inited) return;
  _inited = true;
  try {
    const discarded = wasDiscardedAtStart();
    if (discarded) {
      const rec = logRecovery({ case: 'старт-после-выгрузки', action: 'страница загрузилась заново после ухода в фон' });
      if (shouldNotify(rec)) setTimeout(() => showRecoveryChip(rec), 1500);
      log.warn('[recover] вкладка была выгружена браузером целиком — приложение стартовало заново');
    } else {
      const arr = _read();
      if (arr.length) {
        log.info(`[recover] в журнале ${arr.length} записей с прошлых сеансов — MikhmelViewer.recoveryReport()`);
      }
    }
    // Метки ставим ПОСЛЕ чтения, иначе снимем свою же.
    try { localStorage.removeItem(KEY_HIDDEN_OLD); } catch (e) { /* */ }

    if (typeof document?.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') _clearHidden(); else _setHidden();
      });
    }

    /**
     * [10.1.6] pagehide СНИМАЕТ метку — и это не описка.
     *
     * Как было и почему не помогло. Сначала pagehide метку СТАВИЛ, и обычное
     * закрытие выглядело как выгрузка. В 10.1.5 я это убрал — и стало не
     * лучше: браузер перед выгрузкой страницы сам шлёт visibilitychange со
     * значением hidden. То есть при закрытии и перезагрузке метка всё равно
     * ставилась, просто другим обработчиком.
     *
     * Различить можно так. Уход по-хорошему — закрыли вкладку, нажали
     * перезагрузку, ушли по ссылке — всегда сопровождается pagehide. А когда
     * браузер убивает скрытую вкладку ради памяти, не срабатывает НИЧЕГО:
     * страницу просто не будят. Значит pagehide — признак нормального ухода,
     * и метку на нём надо снимать.
     *
     * Исключение — persisted: страница уехала в кэш «вперёд-назад» (так ведут
     * себя телефоны при сворачивании) и вполне может быть убита потом. Такую
     * метку оставляем.
     */
    if (typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener('pagehide', (e) => { if (!e?.persisted) _clearHidden(); });
    }
    // [10.1.5] Здесь стоял `pagehide` → поставить метку. Но pagehide
    // срабатывает при ЛЮБОМ уходе со страницы: закрыли вкладку, нажали
    // перезагрузку, ушли по ссылке. Метка оставалась непогашенной, и при
    // следующем запуске программа объявляла «вкладку выгрузил браузер» и
    // предлагала вернуть вид — после обычной перезагрузки, на ровном месте.
    //
    // Настоящая выгрузка распознаётся и без него: уход в фон ставит метку
    // через visibilitychange, возвращение её снимает. Если браузер убил
    // вкладку, пока она была скрыта, снимать метку стало некому — она
    // доживает до следующего запуска. Это и есть выгрузка.
  } catch (e) { /* */ }
}

export default {
  logRecovery, getRecoveryReport, showRecoveryReport, showRecoveryChip,
  clearRecoveryLog, hasRecoveryEvents, shouldNotify, detectDiscard,
  noteMemoryEvent, getMemoryNotes, wasDiscardedAtStart,
  getNotifyLevel, setNotifyLevel, NOTIFY_LEVELS, initRecoveryReport,
};
